/**
 * @fileoverview RTK Query bindings for the Apple Music connect surface
 * (WEB-443 · Apple Music Integration M4). Backs the browser MusicKit JS
 * connect + consent flow on `settings/personalization`.
 *
 * Three endpoints mount under `/api/v1/music` (backend API-1495 / API-1496):
 *
 * - `getMusicToken` (query) — `GET /v1/music/token`. Returns the MusicKit
 *   developer token used to bootstrap the browser SDK. Auth-gated but
 *   user-agnostic (the token identifies LOCI to Apple, not the user).
 * - `connectMusic` (mutation) — `POST /v1/music/connect`. Hands the
 *   browser-resolved Music-User-Token to the backend, which validates it
 *   against Apple and persists an encrypted connection. Returns the public
 *   connection status (the token itself is never echoed back).
 * - `getMusicStatus` (query) — `GET /v1/music/status`. Reports whether the
 *   authenticated user has a stored connection. Provides the `MusicConnection`
 *   cache tag so `connectMusic` and `disconnectMusic` refresh it.
 * - `disconnectMusic` (mutation, WEB-445) — `DELETE /v1/music/connect`.
 *   Optimistically clears the cached `getMusicStatus` entry before the
 *   request settles (the settings UI reflects "not connected" immediately);
 *   a failed request rolls the cache back via `patch.undo()` so the UI
 *   returns to "connected" instead of stranding on a false disconnect.
 *
 * CONTRACT SOURCE OF TRUTH — read before "fixing" the shapes below.
 * ------------------------------------------------------------------
 * The wire contract is the **shipped backend**, mirrored by the OpenAPI spec
 * (`specs/openapi/backend-api-v1.yaml`) and the generated `@loci/types` paths.
 * It deliberately does NOT match the early CodeRabbit coding-plan sketch:
 *
 *   - `/token` returns `{ token }`            — NOT `{ developerToken }`.
 *   - `/connect` body is `{ userToken, … }`   — NOT `{ musicUserToken }`.
 *   - `/connect` + `/status` return the shared `MusicConnectionStatus`
 *     `{ connected, storefront?, connectedAt? }` — NOT `{ connected, status }`.
 *
 * `connect`/`status` types are therefore DERIVED from the generated spec so
 * they can never drift from the backend (same discipline as the notification /
 * push bindings in `lociApi.ts`; precedent: API-1436 social-tab white-screen
 * caused by hand-written types diverging from the canonical schema). Only the
 * `/token` response is hand-typed because that route is intentionally absent
 * from the public OpenAPI surface.
 */
import type { BackendApiPaths as paths, RequestBody, ResponseBody } from '@loci/types';
import { lociApi } from '../lociApi.js';

/**
 * Response from `GET /v1/music/token`. Hand-typed because the route is omitted
 * from the public OpenAPI spec by design — it is an internal bootstrap handoff,
 * not part of the documented partner contract. The backend responds with a bare
 * `{ token }` (see `services/backend-api/src/routes/versions/v1/music/index.ts`).
 */
export interface MusicTokenResponse {
  /** The MusicKit developer token (a short-lived signed JWT). */
  token: string;
}

/**
 * Request body for `POST /v1/music/connect`. Derived from the OpenAPI spec so
 * the field name (`userToken`, NOT `musicUserToken`) tracks the backend Zod
 * schema. `storefront` is optional — the backend derives it from a storefront
 * probe when omitted.
 */
export type MusicConnectRequest = RequestBody<paths, '/api/v1/music/connect', 'post'>;

/**
 * Response from `POST /v1/music/connect` (201). The shared
 * `MusicConnectionStatus` — `{ connected, storefront?, connectedAt? }`. The
 * encrypted user token is never returned.
 */
export type MusicConnectResponse = ResponseBody<paths, '/api/v1/music/connect', 'post', 201>;

/**
 * Response from `GET /v1/music/status` (200). The same `MusicConnectionStatus`
 * shape as the connect response, served from one backend serializer so the two
 * cannot drift.
 */
export type MusicStatusResponse = ResponseBody<paths, '/api/v1/music/status', 'get', 200>;

/**
 * Response from `GET /v1/music/taste` (200). The authenticated user's persisted
 * Apple listening taste — `{ topGenres, topArtistNames, lastComputedAt }` —
 * surfaced by the "Your Taste" hub (WEB-449). Derived from the OpenAPI spec so
 * the shape can never drift from the backend. Arrays are always present (empty,
 * never absent) when the user has no synced taste yet.
 */
export type MusicTasteResponse = ResponseBody<paths, '/api/v1/music/taste', 'get', 200>;

/**
 * Response from `GET /v1/music/charts/city` (200). Apple Music city charts
 * grouped by resource type ("What's trending in {City}"; API-1506). Public /
 * catalog-cached — no auth. Derived from the OpenAPI spec.
 */
export type MusicCityChartsResponse = ResponseBody<paths, '/api/v1/music/charts/city', 'get', 200>;

/** Query args for `getMusicCityCharts` — optional Apple storefront (default `us`). */
export interface MusicCityChartsArgs {
  /** Apple Music storefront / region code (2–16 lowercase letters/digits). */
  storefront?: string;
}

const musicEndpoints = lociApi.injectEndpoints({
  endpoints: (builder) => ({
    getMusicToken: builder.query<MusicTokenResponse, void>({
      query: () => 'v1/music/token',
    }),

    connectMusic: builder.mutation<MusicConnectResponse, MusicConnectRequest>({
      query: (body) => ({
        url: 'v1/music/connect',
        method: 'POST',
        body,
      }),
      // The new connection changes the answer to `GET /v1/music/status`; refresh
      // it (and any other `MusicConnection` subscriber) once the connect lands.
      invalidatesTags: [{ type: 'MusicConnection', id: 'SELF' }],
    }),

    getMusicStatus: builder.query<MusicStatusResponse, void>({
      query: () => 'v1/music/status',
      providesTags: [{ type: 'MusicConnection', id: 'SELF' }],
    }),

    // The user's persisted taste is downstream of their connection, so it
    // shares the `MusicConnection/SELF` tag: connecting or disconnecting
    // (which invalidate that tag) also refetch the taste hub content.
    getMusicTaste: builder.query<MusicTasteResponse, void>({
      query: () => 'v1/music/taste',
      providesTags: [{ type: 'MusicConnection', id: 'SELF' }],
    }),

    // Public catalog read — no auth, no cache tag. `storefront` defaults to `us`
    // on the backend when omitted.
    getMusicCityCharts: builder.query<MusicCityChartsResponse, MusicCityChartsArgs | void>({
      query: (arg) => {
        const storefront = arg?.storefront?.trim();
        return storefront
          ? `v1/music/charts/city?storefront=${encodeURIComponent(storefront)}`
          : 'v1/music/charts/city';
      },
    }),

    disconnectMusic: builder.mutation<void, void>({
      query: () => ({
        url: 'v1/music/connect',
        method: 'DELETE',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        // `musicEndpoints` (not `lociApi`) — `getMusicStatus` is injected in
        // this same call, so only the enhanced type returned here knows about
        // it. Referencing the outer `const` from this deferred callback is
        // safe: `onQueryStarted` only runs once a mutation is dispatched,
        // long after `musicEndpoints` has been assigned below.
        const patch = dispatch(
          musicEndpoints.util.updateQueryData('getMusicStatus', undefined, (draft) => {
            draft.connected = false;
            delete draft.storefront;
            delete draft.connectedAt;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: 'MusicConnection', id: 'SELF' }],
    }),
  }),
});

export const {
  useGetMusicTokenQuery,
  useConnectMusicMutation,
  useGetMusicStatusQuery,
  useDisconnectMusicMutation,
  useGetMusicTasteQuery,
  useGetMusicCityChartsQuery,
} = musicEndpoints;

/**
 * Narrow-typed endpoint slice exposed to tests (mirrors the `discover.ts`
 * pattern). `injectEndpoints` registers these on the runtime `lociApi.endpoints`
 * registry, but the base api's static type doesn't know about injected
 * endpoints — this reference lets tests dispatch `endpoints[name].initiate(...)`
 * with an explicit type instead. Not part of the public API.
 */
interface MusicEndpointSlice<TArg, TResult> {
  name: string;
  initiate: (arg: TArg) => (
    dispatch: unknown,
    getState: unknown,
    extra: unknown,
  ) => Promise<{ data?: TResult; error?: unknown; unwrap: () => Promise<TResult> }> & {
    unwrap: () => Promise<TResult>;
    /** Present on query subscriptions; unused for the mutation slice. */
    unsubscribe: () => void;
  };
}

interface MusicTestingEndpoints {
  getMusicToken: MusicEndpointSlice<void, MusicTokenResponse>;
  connectMusic: MusicEndpointSlice<MusicConnectRequest, MusicConnectResponse>;
  getMusicStatus: MusicEndpointSlice<void, MusicStatusResponse>;
  disconnectMusic: MusicEndpointSlice<void, void>;
  getMusicTaste: MusicEndpointSlice<void, MusicTasteResponse>;
  getMusicCityCharts: MusicEndpointSlice<MusicCityChartsArgs | void, MusicCityChartsResponse>;
}

export const __testing = {
  endpoints: musicEndpoints.endpoints as unknown as MusicTestingEndpoints,
};
